import { create } from 'zustand';
import type { GroupingConfig } from '../types/types/view';
import type { DateRangeFilter, TransposeConfig } from '../views/grid/types/gridTypes';

/**
 * Active cell position in the grid
 */
export interface ActiveCell {
  rowId: string;
  fieldId: string;
}

/**
 * Sheet UI State - manages selection, active cell, and grouping state
 * This store handles UI state that doesn't need to be synced with the server.
 * Server state (rows, fields) is managed by React Query in useSheetData hook.
 * Story 4.1: Added grouping state management for multi-level grouping
 * Story 4.3: Added date range filter state
 * Story 4.4: Added transpose state (mutually exclusive with grouping)
 */
export interface SheetUIState {
  // State
  selectedRows: string[];
  activeCell: ActiveCell | null;

  // Grouping state (Story 4.1)
  groupingConfig: GroupingConfig | null;
  expandedGroupIds: string[]; // Array for JSON serialization

  // Date range filter state (Story 4.3)
  dateRangeFilter: DateRangeFilter | null;

  // Transpose state (Story 4.4)
  transposeConfig: TransposeConfig | null;

  // Actions
  setSelectedRows: (ids: string[]) => void;
  setActiveCell: (cell: ActiveCell | null) => void;
  clearSelection: () => void;
  toggleRowSelection: (rowId: string) => void;
  selectRowRange: (startId: string, endId: string, allRowIds: string[]) => void;

  // Grouping actions (Story 4.1)
  setGroupingConfig: (config: GroupingConfig | null) => void;
  toggleGroupExpanded: (groupId: string) => void;
  collapseAllGroups: () => void;
  expandAllGroups: (groupIds: string[]) => void;

  // Date range filter actions (Story 4.3)
  setDateRangeFilter: (filter: DateRangeFilter | null) => void;

  // Transpose actions (Story 4.4)
  setTransposeConfig: (config: TransposeConfig | null) => void;
}

export const useSheetUIStore = create<SheetUIState>((set) => ({
  // Initial state
  selectedRows: [],
  activeCell: null,

  // Grouping state (Story 4.1)
  groupingConfig: null,
  expandedGroupIds: [],

  // Date range filter state (Story 4.3)
  dateRangeFilter: null,

  // Transpose state (Story 4.4)
  transposeConfig: null,

  // Actions
  setSelectedRows: (ids) => set({ selectedRows: ids }),

  setActiveCell: (cell) => set({ activeCell: cell }),

  clearSelection: () => set({ selectedRows: [], activeCell: null }),

  toggleRowSelection: (rowId) =>
    set((state) => ({
      selectedRows: state.selectedRows.includes(rowId)
        ? state.selectedRows.filter((id) => id !== rowId)
        : [...state.selectedRows, rowId],
    })),

  selectRowRange: (startId, endId, allRowIds) =>
    set(() => {
      const startIndex = allRowIds.indexOf(startId);
      const endIndex = allRowIds.indexOf(endId);
      if (startIndex === -1 || endIndex === -1) return {};

      const [from, to] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
      const rangeIds = allRowIds.slice(from, to + 1);
      return { selectedRows: rangeIds };
    }),

  // Grouping actions (Story 4.1)
  // AC-4.4.7: Enabling grouping disables transpose (mutual exclusion)
  setGroupingConfig: (config) =>
    set(() => {
      // When setting new config, initialize expandedGroupIds from config or based on defaultExpanded
      const expandedGroupIds = config?.expandedGroupIds || [];
      return {
        groupingConfig: config,
        expandedGroupIds,
        // Disable transpose when grouping is enabled (AC-4.4.7)
        transposeConfig: config?.enabled ? null : undefined,
      };
    }),

  toggleGroupExpanded: (groupId) =>
    set((state) => {
      const isExpanded = state.expandedGroupIds.includes(groupId);
      const expandedGroupIds = isExpanded
        ? state.expandedGroupIds.filter((id) => id !== groupId)
        : [...state.expandedGroupIds, groupId];

      // Also update groupingConfig to persist state
      const groupingConfig = state.groupingConfig
        ? { ...state.groupingConfig, expandedGroupIds }
        : null;

      return { expandedGroupIds, groupingConfig };
    }),

  collapseAllGroups: () =>
    set((state) => ({
      expandedGroupIds: [],
      groupingConfig: state.groupingConfig
        ? { ...state.groupingConfig, expandedGroupIds: [] }
        : null,
    })),

  expandAllGroups: (groupIds) =>
    set((state) => ({
      expandedGroupIds: groupIds,
      groupingConfig: state.groupingConfig
        ? { ...state.groupingConfig, expandedGroupIds: groupIds }
        : null,
    })),

  // Date range filter actions (Story 4.3)
  setDateRangeFilter: (filter) => set({ dateRangeFilter: filter }),

  // Transpose actions (Story 4.4)
  // AC-4.4.7: Enabling transpose disables grouping (mutual exclusion)
  setTransposeConfig: (config) =>
    set((state) => ({
      transposeConfig: config,
      // Disable grouping when transpose is enabled (AC-4.4.7)
      groupingConfig: config?.enabled
        ? state.groupingConfig ? { ...state.groupingConfig, enabled: false } : null
        : state.groupingConfig,
      expandedGroupIds: config?.enabled ? [] : state.expandedGroupIds,
    })),
}));
